import { Component, OnInit } from '@angular/core';
import { UserService } from '../../../../../../services/user.service';
import { ActivatedRoute } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { UserType } from '../../../../../../model/Const';
import {
  AiSpeakingTopic,
  AiSpeakingTopicsService
} from '../../../../../../services/ai-speaking-topics.service';
import { TwNoticeService } from '../../../../../../services/tw-notice.service';
import { HWResourceService } from '../../../../../../services/huawei-resource.service';

@Component({
    selector: 'app-admin-resource-book-item-ai-speaking-topics',
    templateUrl: './index.component.html',
    styleUrls: ['./index.component.scss'],
    standalone: false
})
export class AdminResourceBookItemAiSpeakingTopicsComponent implements OnInit {
  user: any = null;
  constUserType = UserType;

  isLoading = false;
  book_id = 0;
  currentTopicIndex = 0;
  topicsId = 0;
  topics: AiSpeakingTopic[] = [];
  analyzingTopicIndices = new Set<number>();
  bisType = 'book';

  private readonly defaultOpeningLine = 'I am xiaopongpong. you can say "hello" to start the chat.';

  constructor(
    private userService: UserService,
    private aiSpeakingTopicsService: AiSpeakingTopicsService,
    private activatedRoute: ActivatedRoute,
    private twNoticeService: TwNoticeService,
    private nzMessageService: NzMessageService,
  ) {
    this.user = this.userService.getUserInfo();
    this.bisType = this.activatedRoute.pathFromRoot[2].snapshot.url[2].path;
    this.activatedRoute.params.subscribe(params => {
      this.book_id = +params['item_id'] || 0;
    });
  }

  ngOnInit() {
    if (this.book_id) {
      this.loadTopics();
    }
  }

  private emptyTopic(): AiSpeakingTopic {
    return {
      image: '',
      imageContent: '',
      openingLine: this.defaultOpeningLine,
      description: '',
    };
  }

  private normalizeTopic(topic: Partial<AiSpeakingTopic>): AiSpeakingTopic {
    return {
      ...this.emptyTopic(),
      ...topic,
      image: topic.image || '',
      imageContent: topic.imageContent || '',
      openingLine: topic.openingLine || '',
      description: topic.description || '',
    };
  }

  loadTopics() {
    this.isLoading = true;
    this.aiSpeakingTopicsService.getTopicsByBook(this.book_id).then((result) => {
      this.topicsId = +result.id || 0;
      const loaded = result.topics || [];
      this.topics = loaded.length > 0
        ? loaded.map((topic: Partial<AiSpeakingTopic>) => this.normalizeTopic(topic))
        : [this.emptyTopic()];
      this.isLoading = false;
    }).catch(err => {
      this.isLoading = false;
      this.topics = [this.emptyTopic()];
      this.nzMessageService.error(err.message || '加载失败');
    });
  }

  topicIndexChange(index: number) {
    this.currentTopicIndex = index;
  }

  addTopic() {
    this.topics.push(this.emptyTopic());
    this.currentTopicIndex = this.topics.length - 1;
  }

  removeTopic() {
    if (this.topics.length <= 1) {
      this.nzMessageService.warning('至少保留一个话题');
      return;
    }
    this.topics.splice(this.currentTopicIndex, 1);
    if (this.currentTopicIndex >= this.topics.length) {
      this.currentTopicIndex = this.topics.length - 1;
    }
  }

  onTopicImageUploaded(index: number, event: any) {
    if (event?.url) {
      this.topics[index].image = event.url;
    }
  }

  canAnalyzeTopicImage(index: number): boolean {
    return Boolean(this.topics[index]?.image?.trim());
  }

  isAnalyzingTopicImage(index: number): boolean {
    return this.analyzingTopicIndices.has(index);
  }

  analyzeTopicImage(index: number) {
    const topic = this.topics[index];
    if (!topic?.image?.trim() || this.isAnalyzingTopicImage(index)) {
      return;
    }

    const imageUrl = HWResourceService.resolveMediaUrl(topic.image);
    this.analyzingTopicIndices.add(index);
    this.aiSpeakingTopicsService.analyzeImage(imageUrl).then((result) => {
      topic.imageContent = result?.content || '';
      if (!topic.imageContent) {
        this.nzMessageService.warning('未识别到图片内容');
      }
    }).catch(err => {
      this.nzMessageService.error(err?.message || '图片识别失败');
    }).finally(() => {
      this.analyzingTopicIndices.delete(index);
    });
  }

  validateTopics(): boolean {
    for (let i = 0; i < this.topics.length; i++) {
      const topic = this.topics[i];
      if (!topic.image?.trim()) {
        this.nzMessageService.warning(`话题${i + 1}：请上传图片`);
        this.currentTopicIndex = i;
        return false;
      }
      if (!topic.imageContent?.trim()) {
        this.nzMessageService.warning(`话题${i + 1}：请填写图片内容`);
        this.currentTopicIndex = i;
        return false;
      }
      if (!topic.openingLine?.trim()) {
        this.nzMessageService.warning(`话题${i + 1}：请填写开场白`);
        this.currentTopicIndex = i;
        return false;
      }
      if (!topic.description?.trim()) {
        this.nzMessageService.warning(`话题${i + 1}：请填写话题关键词&句式&话题示例`);
        this.currentTopicIndex = i;
        return false;
      }
    }
    return true;
  }

  saveTopics() {
    if (this.isLoading) {
      return;
    }
    if (!this.validateTopics()) {
      return;
    }

    const payload = {
      bookId: this.book_id,
      topics: this.topics,
    };

    this.isLoading = true;
    this.aiSpeakingTopicsService.saveTopics(payload).then((result) => {
      this.isLoading = false;
      if (result?.id) {
        this.topicsId = +result.id;
      }
      this.twNoticeService.success({ nzContent: '儲存成功' });
    }).catch(err => {
      this.isLoading = false;
      this.nzMessageService.error(err.message || '保存失败');
    });
  }
}
